'use client';

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { TINY_VIEWPORT_WIDTH } from '@/shared/constants';

export type OverflowMenuItems = Record<string, ReactNode | null>;

/**
 * Render order for the drop-down nav menu. Account replaced Cases / Settings /
 * Logout in the top-level IA (TR-3245). Timeline view switchers stay in this
 * list for overflow-level bookkeeping but are stripped before the menu renders
 * — they live in the work-product sub-nav.
 */
export const NAV_MENU_ORDER = [
  'Assistant',
  'WorkProducts',
  'Csam',
  'Upload',
  'Help',
  'Account',
  'Sync',
  'Hotkeys',
  'Presentation',
  'Courtroom',
  'Details',
] as const;

export type NavMenuKey = (typeof NAV_MENU_ORDER)[number];

export type OverflowLevelConfig = {
  level: number;
  breakpoint: number;
  keys: NavMenuKey[];
};

export function buildOverflowLevels(): OverflowLevelConfig[] {
  // Horizontal nav items are wider than the old vertical stacks — collapse earlier.
  return [
    { level: 0.5, breakpoint: 1260, keys: ['Upload', 'Csam', 'Help'] },
    { level: 1, breakpoint: 1120, keys: ['Account', 'Sync', 'Hotkeys'] },
    { level: 2.5, breakpoint: 900, keys: ['Assistant', 'WorkProducts'] },
    { level: 3, breakpoint: 635, keys: ['Presentation', 'Courtroom', 'Details'] },
    { level: 4, breakpoint: 520, keys: [] },
    { level: 5, breakpoint: TINY_VIEWPORT_WIDTH, keys: [] },
  ];
}

type UsePrimaryNavOverflowArgs = {
  clientWidth: number | undefined;
  menuItems: OverflowMenuItems;
  selectedOption: string | null;
};

export function usePrimaryNavOverflow({
  clientWidth,
  menuItems,
  selectedOption,
}: UsePrimaryNavOverflowArgs) {
  const overflowLevels = useMemo(() => buildOverflowLevels(), []);
  const [overflowMenuKeys, setOverflowMenuKeys] = useState<NavMenuKey[]>([]);
  const [overflowLevel, setOverflowLevel] = useState(0);

  useEffect(() => {
    if (!clientWidth) return;

    const collected = new Set<NavMenuKey>();
    let newOverflowLevel = 0;

    for (const level of overflowLevels) {
      if (clientWidth < level.breakpoint) {
        for (const key of level.keys) {
          collected.add(key);
        }
        newOverflowLevel = level.level;
      }
    }

    // The timeline view switchers collapse in at level 3 but are not offered here —
    // they live in the work-product sub-nav.
    collected.delete('Details');
    collected.delete('Courtroom');
    collected.delete('Presentation');

    if (!selectedOption) {
      collected.delete('Assistant');
      collected.delete('WorkProducts');
      collected.delete('Csam');
    }

    // A key with no rendered item (feature-flagged off) never reaches the menu.
    const ordered = NAV_MENU_ORDER.filter(
      (key) => collected.has(key) && menuItems[key] != null,
    );

    setOverflowMenuKeys(ordered);
    setOverflowLevel(newOverflowLevel);
  }, [clientWidth, menuItems, overflowLevels, selectedOption]);

  return { overflowLevel, overflowMenuKeys, overflowLevels };
}
